// Thin Radix Dialog wrapper preserving the Fajr Arabi token system.
// Re-exports Radix primitives so consumers can compose Trigger/Close/etc. directly.

import * as RadixDialog from '@radix-ui/react-dialog';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from './cn';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;
export const DialogPortal = RadixDialog.Portal;

export const DialogOverlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(function DialogOverlay({ className, ...rest }, ref) {
  return (
    <RadixDialog.Overlay
      ref={ref}
      className={cn('fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm', className)}
      {...rest}
    />
  );
});

export interface DialogContentProps extends Omit<
  ComponentPropsWithoutRef<typeof RadixDialog.Content>,
  'title'
> {
  title?: ReactNode;
  description?: ReactNode;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(function DialogContent(
  { className, children, title, description, ...rest },
  ref,
) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <RadixDialog.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2',
          'rounded-3xl bg-cream-50 p-6 shadow-[0_30px_80px_-20px_rgba(31,26,23,0.4)] ring-1 ring-burgundy-500/10',
          'focus:outline-none',
          className,
        )}
        {...rest}
      >
        {(title || description) && (
          <div className="mb-4">
            {title && (
              <RadixDialog.Title className="text-xl font-bold text-ink-900">
                {title}
              </RadixDialog.Title>
            )}
            {description && (
              <RadixDialog.Description className="mt-1 text-sm text-ink-700">
                {description}
              </RadixDialog.Description>
            )}
          </div>
        )}
        {children}
      </RadixDialog.Content>
    </DialogPortal>
  );
});

export const DialogTitle = RadixDialog.Title;
export const DialogDescription = RadixDialog.Description;
